/**
 * Client-side copy of no-flights-5d match rules + by-operator email template
 * (mirrors api/no-flights-five-days.mjs).
 */

function cleanText(v) {
  if (v == null) return "";
  return String(v).trim();
}

function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export const NO_FLIGHTS_FIVE_DAYS_MINUTES = 5 * 24 * 60;

export const NO_FLIGHTS_NEW_MIN_DAYS = 5;
export const NO_FLIGHTS_NEW_MAX_DAYS = 11;

export const NO_FLIGHTS_FIVE_DAYS_LEGEND =
  "All aircraft with no landings recorded in the last 5 days (including AOG). Aircraft with Transition status or Russia status are not included. Red MSN = newly on this list since the previous weekly send (5–11 days on ground).";

export const NO_FLIGHTS_BY_OPERATOR_EMAIL_SUBJECT_BASE = "A/C WITH NO ACTIVITY IN 5+ DAYS BY OPERATOR";

/** @deprecated use noFlightsByOperatorEmailSubject(date) — kept for callers expecting a string constant. */
export const NO_FLIGHTS_BY_OPERATOR_EMAIL_SUBJECT = NO_FLIGHTS_BY_OPERATOR_EMAIL_SUBJECT_BASE;

/** Dubai calendar date parts (same basis as Operator Progress week subjects). */
function dubaiYmd(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Dubai",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date instanceof Date ? date : new Date(date));
  return {
    y: Number(parts.find((p) => p.type === "year")?.value),
    m: Number(parts.find((p) => p.type === "month")?.value),
    d: Number(parts.find((p) => p.type === "day")?.value),
  };
}

function isoWeekYearFromYmd(y, m, d) {
  const dt = new Date(Date.UTC(y, m - 1, d));
  const day = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((dt - yearStart) / 86400000 + 1) / 7);
  return { week, year: dt.getUTCFullYear() };
}

/** ISO week in Asia/Dubai — matches Operator Progress `WK n-YYYY`. */
export function noFlightsReportWeekMeta(date = new Date()) {
  const { y, m, d } = dubaiYmd(date);
  return isoWeekYearFromYmd(y, m, d);
}

export function noFlightsByOperatorEmailSubject(date = new Date()) {
  const { week, year } = noFlightsReportWeekMeta(date);
  return `${NO_FLIGHTS_BY_OPERATOR_EMAIL_SUBJECT_BASE} WK ${week}-${year}`;
}

/** Whole days only (no hours/weeks/fractions) for the by-operator email. */
export function formatTogWholeDays(minutesOrLabel) {
  if (typeof minutesOrLabel === "number" && Number.isFinite(minutesOrLabel)) {
    const days = Math.max(0, Math.floor(minutesOrLabel / (24 * 60)));
    return `${days}d`;
  }
  const s = cleanText(minutesOrLabel);
  if (!s || s === "—") return "—";
  const asMinutes = Number(s);
  if (Number.isFinite(asMinutes) && asMinutes >= 0) {
    return `${Math.floor(asMinutes / (24 * 60))}d`;
  }
  // Already a label like "12d", "2w 3d", "10.5h" — coerce known patterns to whole days.
  const dayMatch = s.match(/^(\d+)\s*d\b/i);
  if (dayMatch) return `${Number(dayMatch[1])}d`;
  const weekMatch = s.match(/^(\d+)\s*w(?:\s+(\d+)\s*d)?$/i);
  if (weekMatch) {
    const days = Number(weekMatch[1]) * 7 + (weekMatch[2] ? Number(weekMatch[2]) : 0);
    return `${days}d`;
  }
  const moMatch = s.match(/^(\d+)\s*mo(?:\s+(\d+)\s*w)?$/i);
  if (moMatch) {
    const days = Number(moMatch[1]) * 30 + (moMatch[2] ? Number(moMatch[2]) * 7 : 0);
    return `${days}d`;
  }
  const hourMatch = s.match(/^(\d+(?:\.\d+)?)\s*h/i);
  if (hourMatch) {
    return `${Math.floor(Number(hourMatch[1]) / 24)}d`;
  }
  return s;
}

/** Bare day count for the Excel Time-on-ground column. */
export function formatTogDaysAmount(minutesOrLabel) {
  const label = formatTogWholeDays(minutesOrLabel);
  if (!label || label === "—") return "—";
  const m = String(label).match(/^(\d+)\s*d$/i);
  return m ? m[1] : label;
}

export const NO_FLIGHTS_BY_OPERATOR_EXCEL_HEADERS = [
  "MSN",
  "Registration",
  "Operator",
  "FM",
  "Status",
  "TAM status",
  "Time on ground (DAYS)",
  "Location",
  "Comments",
];

const NO_FLIGHTS_PREV_MSNS_KEY = "actracker.v2.noFlights5d.prevMsns";

export function loadPreviousNoFlightsMsns() {
  try {
    const raw = localStorage.getItem(NO_FLIGHTS_PREV_MSNS_KEY);
    if (!raw) return null;
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return null;
    return new Set(list.map((x) => cleanText(x)).filter(Boolean));
  } catch {
    return null;
  }
}

export function savePreviousNoFlightsMsns(msns) {
  try {
    const list = Array.from(
      new Set([...(msns || [])].map((x) => cleanText(x)).filter((x) => x && x !== "—"))
    );
    localStorage.setItem(NO_FLIGHTS_PREV_MSNS_KEY, JSON.stringify(list));
  } catch {
    /* ignore quota / private mode */
  }
}

export function isNewOnNoFlightsReport(row, previousMsns = null) {
  const msn = cleanText(row?.msn);
  if (previousMsns instanceof Set) {
    if (!msn || msn === "—") return false;
    return !previousMsns.has(msn);
  }
  const minutes = Number.isFinite(row?.togMinutes) ? row.togMinutes : null;
  let days = null;
  if (Number.isFinite(minutes)) {
    days = Math.floor(minutes / (24 * 60));
  } else {
    const raw = cleanText(row?.tog);
    if (/^\d+$/.test(raw)) {
      days = Number(raw);
    } else {
      const amount = formatTogDaysAmount(row?.tog);
      if (amount && amount !== "—") {
        const n = Number(amount);
        if (Number.isFinite(n)) days = n;
      }
    }
  }
  if (!Number.isFinite(days)) return false;
  return days >= NO_FLIGHTS_NEW_MIN_DAYS && days <= NO_FLIGHTS_NEW_MAX_DAYS;
}

export function noFlightsByOperatorExcelCells(row) {
  const fm = fleetManagerInitials(row?.fm || row?.owner);
  const tog = Number.isFinite(row?.togMinutes)
    ? formatTogDaysAmount(row.togMinutes)
    : formatTogDaysAmount(row?.tog);
  return [
    cleanText(row?.msn) || "—",
    cleanText(row?.reg) || "—",
    cleanText(row?.operator) || "—",
    fm,
    cleanText(row?.status) || "—",
    normaliseTamLabel(row?.tam),
    tog,
    cleanText(row?.loc) || "—",
    cleanText(row?.comments) || "—",
  ];
}

export function operatorMergeSpans(sortedRows = []) {
  const spans = new Array(sortedRows.length).fill(0);
  let i = 0;
  while (i < sortedRows.length) {
    const key = cleanText(sortedRows[i]?.operator) || "—";
    let j = i + 1;
    while (j < sortedRows.length && (cleanText(sortedRows[j]?.operator) || "—") === key) j += 1;
    spans[i] = j - i;
    i = j;
  }
  return spans;
}

/** True on the last aircraft row of an operator block (after sort). */
export function isNoFlightsOperatorGroupEnd(spans, index) {
  return index === (spans || []).length - 1 || Number(spans[index + 1]) > 0;
}

function withOperatorGroupRule(style, isGroupEnd) {
  return isGroupEnd ? `${style}border-bottom:1px solid #9ca3af;` : style;
}

function xmlEscape(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/\r\n|\r|\n/g, "&#10;");
}

/**
 * SpreadsheetML workbook with a single By-operator sheet:
 * merged Operator cells, days-only TOG, red MSN for newly listed aircraft.
 */
export function buildNoFlightsByOperatorExcelXml({
  rows = [],
  title = "No flights 5d — by operator",
  previousMsns = null,
  generatedAt = new Date(),
} = {}) {
  const headers = NO_FLIGHTS_BY_OPERATOR_EXCEL_HEADERS;
  const stamp = generatedAt instanceof Date ? generatedAt : new Date(generatedAt);
  const sorted = sortNoFlightsRowsByOperator(rows);
  const spans = operatorMergeSpans(sorted);
  const commentsIdx = headers.indexOf("Comments");
  const operatorIdx = headers.indexOf("Operator");
  const body = [];
  for (let i = 0; i < sorted.length; i++) {
    const cells = noFlightsByOperatorExcelCells(sorted[i]);
    const isNew = isNewOnNoFlightsReport(sorted[i], previousMsns);
    const span = spans[i];
    const isGroupEnd = isNoFlightsOperatorGroupEnd(spans, i);
    body.push('<Row ss:AutoFitHeight="1">');
    for (let c = 0; c < cells.length; c++) {
      if (c === operatorIdx && span === 0) continue;
      const attrs = [];
      if (c === 0 && isNew) attrs.push(`ss:StyleID="${isGroupEnd ? "MsnNewGroupEnd" : "MsnNew"}"`);
      else if (c === commentsIdx) attrs.push(`ss:StyleID="${isGroupEnd ? "CellWrapGroupEnd" : "CellWrap"}"`);
      else if (c === operatorIdx) attrs.push('ss:StyleID="OperatorMerge"');
      else attrs.push(`ss:StyleID="${isGroupEnd ? "CellGroupEnd" : "Cell"}"`);
      if (c === operatorIdx && span > 1) attrs.push(`ss:MergeDown="${span - 1}"`);
      if (c === operatorIdx + 1 && span === 0) attrs.push(`ss:Index="${c + 1}"`);
      body.push(
        `<Cell ${attrs.join(" ")}><Data ss:Type="String">${xmlEscape(cells[c] || "")}</Data></Cell>`
      );
    }
    body.push("</Row>");
  }

  const columns = headers
    .map((h, i) => {
      const width =
        i === commentsIdx ? 280 : h === "Operator" ? 140 : h === "Registration" || h === "Location" ? 110 : 80;
      return `<Column ss:Width="${width}"/>`;
    })
    .join("");
  const headerRow = `<Row ss:Height="22">${headers
    .map((h) => `<Cell ss:StyleID="Header"><Data ss:Type="String">${xmlEscape(h)}</Data></Cell>`)
    .join("")}</Row>`;
  const meta = [
    `<Row><Cell ss:StyleID="Title" ss:MergeAcross="${headers.length - 1}"><Data ss:Type="String">${xmlEscape(title)}</Data></Cell></Row>`,
    `<Row><Cell ss:StyleID="Meta" ss:MergeAcross="${headers.length - 1}"><Data ss:Type="String">${xmlEscape(NO_FLIGHTS_FIVE_DAYS_LEGEND)}</Data></Cell></Row>`,
    `<Row><Cell ss:StyleID="Meta" ss:MergeAcross="${headers.length - 1}"><Data ss:Type="String">${xmlEscape(`Total: ${sorted.length} aircraft · Generated ${stamp.toISOString()}`)}</Data></Cell></Row>`,
    `<Row><Cell ss:StyleID="Meta" ss:MergeAcross="${headers.length - 1}"><Data ss:Type="String">${xmlEscape("Red MSN = newly on this list since the last Excel download. Operator column is merged per operator.")}</Data></Cell></Row>`,
  ].join("");

  const totalRows = sorted.length + 1 + 4;
  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
 xmlns:o="urn:schemas-microsoft-com:office:office"
 xmlns:x="urn:schemas-microsoft-com:office:excel"
 xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
 <Styles>
  <Style ss:ID="Default" ss:Name="Normal"><Alignment ss:Vertical="Top"/></Style>
  <Style ss:ID="Header"><Font ss:Bold="1" ss:Color="#FFFFFF"/><Interior ss:Color="#C00000" ss:Pattern="Solid"/><Alignment ss:Horizontal="Center" ss:Vertical="Center" ss:WrapText="1"/></Style>
  <Style ss:ID="Cell"><Alignment ss:Vertical="Top" ss:WrapText="1"/></Style>
  <Style ss:ID="CellWrap"><Alignment ss:Vertical="Top" ss:WrapText="1"/></Style>
  <Style ss:ID="OperatorMerge"><Alignment ss:Vertical="Center" ss:Horizontal="Left" ss:WrapText="1"/><Font ss:Bold="1"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#9CA3AF"/></Borders></Style>
  <Style ss:ID="MsnNew"><Alignment ss:Vertical="Top"/><Font ss:Bold="1" ss:Color="#FFFFFF"/><Interior ss:Color="#C00000" ss:Pattern="Solid"/></Style>
  <Style ss:ID="CellGroupEnd"><Alignment ss:Vertical="Top" ss:WrapText="1"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#9CA3AF"/></Borders></Style>
  <Style ss:ID="CellWrapGroupEnd"><Alignment ss:Vertical="Top" ss:WrapText="1"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#9CA3AF"/></Borders></Style>
  <Style ss:ID="MsnNewGroupEnd"><Alignment ss:Vertical="Top"/><Font ss:Bold="1" ss:Color="#FFFFFF"/><Interior ss:Color="#C00000" ss:Pattern="Solid"/><Borders><Border ss:Position="Bottom" ss:LineStyle="Continuous" ss:Weight="1" ss:Color="#9CA3AF"/></Borders></Style>
  <Style ss:ID="Title"><Font ss:Bold="1" ss:Size="14" ss:Color="#1E1F24"/></Style>
  <Style ss:ID="Meta"><Font ss:Color="#4B5563"/></Style>
 </Styles>
 <Worksheet ss:Name="By operator">
  <Table ss:ExpandedColumnCount="${headers.length}" ss:ExpandedRowCount="${totalRows}">
   ${columns}
   ${meta}
   ${headerRow}
   ${body.join("")}
  </Table>
  <AutoFilter x:Range="R5C1:R${totalRows}C${headers.length}" xmlns="urn:schemas-microsoft-com:office:excel"/>
  <WorksheetOptions xmlns="urn:schemas-microsoft-com:office:excel">
   <FreezePanes/><FrozenNoSplit/>
   <SplitHorizontal>5</SplitHorizontal>
   <TopRowBottomPane>5</TopRowBottomPane>
   <ActivePane>2</ActivePane>
  </WorksheetOptions>
 </Worksheet>
</Workbook>`;
}

/** FM column: fleet owner codes are already initials; fall back to name initials. */
export function fleetManagerInitials(owner) {
  const s = cleanText(owner);
  if (!s) return "—";
  if (/^[A-Za-z]{1,4}$/.test(s)) return s.toUpperCase();
  const initials = s
    .split(/[\s,/|-]+/)
    .map((w) => w[0])
    .filter(Boolean)
    .join("")
    .toUpperCase();
  return initials.slice(0, 4) || s;
}

export function normaliseTamLabel(value) {
  const v = cleanText(value).toUpperCase();
  if (v === "CHECK WITH DM") return "CHECK WITH FM";
  return v || "—";
}

const TAM_PILL_OUTLOOK = {
  "IN SERVICE": { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
  AOG: { bg: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
  MAINTENANCE: { bg: "#faf5ff", color: "#7e22ce", border: "#e9d5ff" },
  TRANSITION: { bg: "#fdf2f8", color: "#be185d", border: "#fbcfe8" },
  INCIDENT: { bg: "#fffbeb", color: "#b45309", border: "#fde68a" },
  STORAGE: { bg: "#f9fafb", color: "#374151", border: "#e5e7eb" },
  RUSSIA: { bg: "#eff6ff", color: "#1e3a8a", border: "#bfdbfe" },
  "CHECK WITH FM": { bg: "#ecfeff", color: "#0891b2", border: "#a5f3fc" },
  "CHECK WITH DM": { bg: "#ecfeff", color: "#0891b2", border: "#a5f3fc" },
  "AWAITING OPERATOR FEEDBACK": { bg: "#fefce8", color: "#ca8a04", border: "#fef08a" },
  "NON-DAE": { bg: "#fff7ed", color: "#ea580c", border: "#fed7aa" },
  UNKNOWN: { bg: "#f9fafb", color: "#111827", border: "#e5e7eb" },
};

function outlookTamPillHtml(tam) {
  const t = normaliseTamLabel(tam);
  const c = TAM_PILL_OUTLOOK[t] || TAM_PILL_OUTLOOK.UNKNOWN;
  const wrap =
    t === "AWAITING OPERATOR FEEDBACK"
      ? "white-space:normal;max-width:140px;text-align:center;line-height:1.2;"
      : "white-space:nowrap;";
  return `<span style="display:inline-block;padding:2px 10px;border-radius:999px;font-size:11px;font-weight:600;background:${c.bg};color:${c.color};border:1px solid ${c.border};${wrap}">${escapeHtml(t)}</span>`;
}

export function isRussianFleetAircraft(aircraft) {
  const v = aircraft?.is_russian_fleet;
  if (v === true) return true;
  if (v === false || v == null) return false;
  const s = String(v).trim().toLowerCase();
  return s === "true" || s === "t" || s === "1" || s === "yes" || s === "y";
}

/** TAM buckets included in the 5-day no-activity view (includes AOG; excludes Transition/Russia). */
export function tamIncludedInNoFlightsFiveDays(tam) {
  const t = normaliseTamLabel(tam);
  if (t === "TRANSITION") return false;
  if (t === "RUSSIA") return false;
  return true;
}

export function matchesNoFlightsFiveDays(aircraft, faInterp) {
  if (!aircraft || typeof aircraft !== "object") return false;
  if (isRussianFleetAircraft(aircraft)) return false;

  const tam =
    cleanText(aircraft.tam_status) ||
    cleanText(aircraft.tamStatus) ||
    cleanText(aircraft.status_text) ||
    "—";
  if (!tamIncludedInNoFlightsFiveDays(tam)) return false;

  if (faInterp?.airborne) return false;
  const tog = faInterp?.timeOnGroundMinutes;
  if (!Number.isFinite(tog)) {
    // No landing on record (FA, FR24, last_flight_at): no known activity, so list it as UNKNOWN.
    const manualMs = Date.parse(cleanText(aircraft.manual_status_last_flight));
    if (Number.isFinite(manualMs) && Date.now() - manualMs < NO_FLIGHTS_FIVE_DAYS_MINUTES * 60 * 1000) {
      return false;
    }
    return true;
  }
  return tog >= NO_FLIGHTS_FIVE_DAYS_MINUTES;
}

export function countAircraftByTam(aircraftRows, { tamGetter } = {}) {
  const getTam =
    typeof tamGetter === "function"
      ? tamGetter
      : (a) => normaliseTamLabel(a?.tam_status || a?.tamStatus || a?.tam || "—");
  const counts = new Map();
  for (const row of aircraftRows || []) {
    const tam = getTam(row);
    counts.set(tam, (counts.get(tam) || 0) + 1);
  }
  return counts;
}

/** Ground minutes for sort: prefer togMinutes, else parse a TOG label into whole-day minutes. */
export function togMinutesFromNoFlightsRow(row) {
  if (Number.isFinite(row?.togMinutes)) return row.togMinutes;
  const amount = formatTogDaysAmount(row?.tog);
  if (amount && amount !== "—") {
    const n = Number(amount);
    if (Number.isFinite(n)) return n * 24 * 60;
  }
  return null;
}

function compareNoFlightsDaysThenReg(a, b) {
  const aMin = togMinutesFromNoFlightsRow(a);
  const bMin = togMinutesFromNoFlightsRow(b);
  const aVal = Number.isFinite(aMin) ? aMin : Number.POSITIVE_INFINITY;
  const bVal = Number.isFinite(bMin) ? bMin : Number.POSITIVE_INFINITY;
  // Fewest days of no activity first; unknown TOG last.
  if (aVal !== bVal) return aVal - bVal;
  return cleanText(a?.reg).localeCompare(cleanText(b?.reg), undefined, { sensitivity: "base" });
}

/**
 * Operator A→Z, then fewest days of no activity first within that operator (then registration).
 * Used by the 5-day table, Excel, and email so every export matches the screen.
 */
export function compareNoFlightsRowsByOperator(a, b) {
  const op = cleanText(a?.operator).localeCompare(cleanText(b?.operator), undefined, { sensitivity: "base" });
  if (op !== 0) return op;
  return compareNoFlightsDaysThenReg(a, b);
}

export function sortNoFlightsRowsByOperator(rows = []) {
  return [...(rows || [])].sort(compareNoFlightsRowsByOperator);
}

/**
 * Group by operator A→Z. Aircraft within an operator: fewest days of no activity first.
 */
export function groupNoFlightsRowsByOperator(rows = []) {
  const map = new Map();
  for (const row of rows || []) {
    const op = cleanText(row?.operator) || "—";
    if (!map.has(op)) map.set(op, []);
    map.get(op).push(row);
  }
  return Array.from(map.entries())
    .map(([operator, aircraft]) => ({
      operator,
      aircraft: [...aircraft].sort(compareNoFlightsDaysThenReg),
      tamCounts: countAircraftByTam(aircraft, { tamGetter: (r) => normaliseTamLabel(r.tam) }),
    }))
    .sort((a, b) => a.operator.localeCompare(b.operator, undefined, { sensitivity: "base" }));
}

/** Flatten groups in operator A→Z order for the aircraft detail table. */
export function flattenNoFlightsRowsByOperatorCount(rows = []) {
  return groupNoFlightsRowsByOperator(rows).flatMap((g) => g.aircraft);
}

function collectTamColumns(groups) {
  const set = new Set();
  for (const g of groups || []) {
    for (const tam of g.tamCounts.keys()) set.add(tam);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

function commentsHtmlCell(comments) {
  const text = cleanText(comments);
  if (!text) return "—";
  // Newest-first lines are joined with --- in the Excel helper; keep that order in HTML.
  return escapeHtml(text).replace(/\r\n|\r|\n/g, "<br>");
}

/** Aircraft-list rows with Operator cells merged (rowspan), matching the Excel By-operator sheet. */
function noFlightsByOperatorEmailAircraftRows(sortedRows, { td, tdCenter, tdComments }) {
  const spans = operatorMergeSpans(sortedRows);
  const tdOp = `${td}font-weight:700;vertical-align:middle;background:#f3f4f6;`;
  const parts = [];
  for (let i = 0; i < (sortedRows || []).length; i++) {
    const r = sortedRows[i];
    const span = spans[i];
    const isGroupEnd = isNoFlightsOperatorGroupEnd(spans, i);
    const cell = withOperatorGroupRule(td, isGroupEnd);
    const cellCenter = withOperatorGroupRule(tdCenter, isGroupEnd);
    const cellComments = withOperatorGroupRule(tdComments, isGroupEnd);
    const tog = Number.isFinite(r?.togMinutes)
      ? formatTogDaysAmount(r.togMinutes)
      : formatTogDaysAmount(r?.tog);
    const fm = fleetManagerInitials(r?.fm || r?.owner);
    const opCell =
      span === 0
        ? ""
        : `<td style="${withOperatorGroupRule(tdOp, true)}" rowspan="${span}">${escapeHtml(cleanText(r.operator) || "—")}</td>`;
    parts.push(`<tr>
        ${opCell}
        <td style="${cellCenter}font-family:monospace;">${escapeHtml(fm)}</td>
        <td style="${cell}font-family:monospace;">${escapeHtml(r.msn)}</td>
        <td style="${cell}font-family:monospace;">${escapeHtml(r.reg)}</td>
        <td style="${cell}">${outlookTamPillHtml(r.tam)}</td>
        <td style="${cell}">${escapeHtml(tog)}</td>
        <td style="${cell}">${escapeHtml(r.loc || "—")}</td>
        <td style="${cellComments}">${commentsHtmlCell(r.comments)}</td>
      </tr>`);
  }
  return parts.join("");
}

/**
 * HTML email body: operator×status summary table, then aircraft detail table.
 */
export function buildNoFlightsFiveDaysByOperatorEmailHtml({ rows = [], generatedAt = new Date() } = {}) {
  const stamp = generatedAt instanceof Date ? generatedAt : new Date(generatedAt);
  const subject = noFlightsByOperatorEmailSubject(stamp);
  const groups = groupNoFlightsRowsByOperator(rows);
  const detailRowsData = flattenNoFlightsRowsByOperatorCount(rows);
  const operatorCount = groups.length;
  const tamColumns = collectTamColumns(groups);
  const aircraftTotal = detailRowsData.length;

  const th =
    "padding:8px 10px;text-align:left;font-size:11px;text-transform:uppercase;background:#c00000;color:#fff;border:1px solid #a30000;white-space:nowrap;";
  const thCenter = `${th}text-align:center;`;
  const td = "padding:8px 10px;font-size:13px;border:1px solid #e5e7eb;vertical-align:top;";
  const tdCenter = `${td}text-align:center;`;
  const tdComments = `${td}white-space:normal;max-width:320px;`;

  const summaryHeader = [
    `<th style="${th}">Operator</th>`,
    `<th style="${thCenter}">Total on ground 5+ days</th>`,
    ...tamColumns.map((tam) => `<th style="${thCenter}">${escapeHtml(tam)}</th>`),
  ].join("");

  const summaryRows = groups
    .map(({ operator, aircraft, tamCounts }) => {
      const statusCells = tamColumns
        .map((tam) => {
          const n = tamCounts.get(tam) || 0;
          return `<td style="${tdCenter}">${n ? n : "—"}</td>`;
        })
        .join("");
      return `<tr>
        <td style="${td}">${escapeHtml(operator)}</td>
        <td style="${tdCenter}font-weight:700;">${aircraft.length}</td>
        ${statusCells}
      </tr>`;
    })
    .join("");

  const colSpan = 2 + tamColumns.length;
  const detailRows = noFlightsByOperatorEmailAircraftRows(detailRowsData, { td, tdCenter, tdComments });

  return `<!DOCTYPE html><html><body style="font-family:Calibri,Arial,sans-serif;color:#111827;margin:0;padding:16px;">
    <h1 style="font-size:20px;margin:0 0 8px;color:#c00000;">${escapeHtml(subject)}</h1>
    <p style="font-size:15px;font-weight:600;color:#111827;margin:0 0 8px;">Total: ${aircraftTotal} aircraft · ${operatorCount} operator${operatorCount === 1 ? "" : "s"}</p>
    <p style="font-size:13px;color:#6b7280;margin:0 0 12px;">Generated ${escapeHtml(stamp.toISOString())}</p>
    <p style="font-size:13px;color:#374151;margin:0 0 16px;">${escapeHtml(NO_FLIGHTS_FIVE_DAYS_LEGEND)}</p>
    <div style="font-size:14px;font-weight:700;color:#c00000;text-transform:uppercase;margin:0 0 8px;">By operator</div>
    <table style="width:auto;border-collapse:collapse;margin:0 0 24px;">
      <thead><tr>${summaryHeader}</tr></thead>
      <tbody>${summaryRows || `<tr><td colspan="${colSpan}" style="${td}color:#6b7280;">No matching aircraft.</td></tr>`}</tbody>
    </table>
    <div style="font-size:14px;font-weight:700;color:#c00000;text-transform:uppercase;margin:0 0 8px;">Aircraft list</div>
    <table style="width:100%;border-collapse:collapse;">
      <thead><tr>
        <th style="${th}">Operator</th>
        <th style="${th}">FM</th>
        <th style="${th}">MSN</th>
        <th style="${th}">Reg</th>
        <th style="${th}">Status</th>
        <th style="${th}">Time on ground (DAYS)</th>
        <th style="${th}">Location</th>
        <th style="${th}">Comments</th>
      </tr></thead>
      <tbody>${detailRows || `<tr><td colspan="8" style="${td}color:#6b7280;">No aircraft in this view.</td></tr>`}</tbody>
    </table>
  </body></html>`;
}

/**
 * Build Outlook-compatible unsent draft (.eml).
 * X-Unsent must be first; keep a simple text/html body (no Message-ID / multipart)
 * so classic Outlook opens it as an editable new draft.
 */
export function buildNoFlightsFiveDaysByOperatorEml({ rows = [], generatedAt = new Date() } = {}) {
  const stamp = generatedAt instanceof Date ? generatedAt : new Date(generatedAt);
  const subject = noFlightsByOperatorEmailSubject(stamp);
  const html = buildNoFlightsFiveDaysByOperatorEmailHtml({ rows, generatedAt: stamp });
  const { week, year } = noFlightsReportWeekMeta(stamp);
  const lines = [
    "X-Unsent: 1",
    "From: ",
    "To: ",
    `Subject: ${subject}`,
    "MIME-Version: 1.0",
    'Content-Type: text/html; charset="utf-8"',
    "Content-Transfer-Encoding: 8bit",
    "",
    html,
    "",
  ];
  // LF-only newlines work more reliably with Outlook draft open than CRLF for some builds.
  return {
    subject,
    fileName: `ACTracker-no-activity-5d-by-operator-WK${week}-${year}.eml`,
    blob: new Blob([lines.join("\n")], { type: "message/rfc822" }),
  };
}

/** TAMs that warrant an FM follow-up email from the 5-day no-activity list. */
export const NO_FLIGHTS_FM_FOLLOWUP_TAMS = new Set(["IN SERVICE", "AWAITING OPERATOR FEEDBACK"]);

export function isNoFlightsFmFollowUpTam(tam) {
  return NO_FLIGHTS_FM_FOLLOWUP_TAMS.has(normaliseTamLabel(tam));
}

export function noFlightsFmFollowUpSubject(date = new Date()) {
  const { week, year } = noFlightsReportWeekMeta(date);
  return `Aircraft with no activity for 5+ days. WK ${week}-${year}`;
}

export const NO_FLIGHTS_FM_FOLLOWUP_CC = "datamanagement@dubaiaerospace.com";

/**
 * Group no-flights rows by FM (owner initials), with TAM counts.
 * `followUpAircraft` = IN SERVICE / AWAITING OPERATOR FEEDBACK only.
 */
export function groupNoFlightsRowsByFm(rows = []) {
  const map = new Map();
  for (const row of rows || []) {
    const fm = cleanText(row?.fm || row?.owner) || "—";
    if (!map.has(fm)) map.set(fm, []);
    map.get(fm).push(row);
  }
  return Array.from(map.entries())
    .map(([fm, aircraft]) => {
      const tamCounts = countAircraftByTam(aircraft, { tamGetter: (r) => normaliseTamLabel(r.tam) });
      const followUpAircraft = aircraft.filter((r) => isNoFlightsFmFollowUpTam(r.tam));
      return {
        fm,
        aircraft,
        tamCounts,
        followUpAircraft,
        followUpCount: followUpAircraft.length,
        canEmail: followUpAircraft.length > 0,
      };
    })
    .sort((a, b) => {
      const byFollow = Number(b.canEmail) - Number(a.canEmail);
      if (byFollow !== 0) return byFollow;
      return a.fm.localeCompare(b.fm, undefined, { sensitivity: "base" });
    });
}

/** Same location sources as the no-flights / fleet table (loc, then manual, then DB). */
export function resolveNoFlightsFollowUpLocation(row = {}) {
  const raw = row?.__raw && typeof row.__raw === "object" ? row.__raw : null;
  const candidates = [
    row?.loc,
    row?.location,
    row?.manual_status_location,
    row?.last_known_location,
    raw?.manual_status_location,
    raw?.last_known_location,
    raw?.location,
    raw?.base,
  ];
  for (const c of candidates) {
    const t = cleanText(c);
    if (t && t !== "—") return t;
  }
  return "—";
}

export function buildNoFlightsFmFollowUpEmailHtml({
  fmName = "",
  firstName = "",
  rows = [],
  generatedAt = new Date(),
} = {}) {
  const stamp = generatedAt instanceof Date ? generatedAt : new Date(generatedAt);
  const subject = noFlightsFmFollowUpSubject(stamp);
  const greetingName = cleanText(firstName) || cleanText(fmName) || "colleague";
  const n = (rows || []).length;

  return buildDaeInactivityFollowUpHtml({
    subject,
    greetingName,
    introHtml: `This is a quick follow-up on <strong>${n}</strong> aircraft under your portfolio that are reported with
      <strong>no activity for at least 5 days</strong> and are still marked
      <strong>In Service</strong> or <strong>Awaiting Operator Feedback</strong>.`,
    askHtml:
      "Could you please review the status of each aircraft below and advise on the current situation or expected next flight?",
    rows,
    generatedAt: stamp,
  });
}

/**
 * Shared DAE HTML shell used by FM portfolio drafts and single-aircraft operator status checks.
 */
export function buildDaeInactivityFollowUpHtml({
  subject = "",
  greetingName = "colleague",
  introHtml = "",
  askHtml = "",
  rows = [],
  generatedAt: _generatedAt = new Date(),
} = {}) {
  const th =
    "padding:10px 12px;text-align:left;font-size:11px;letter-spacing:0.04em;text-transform:uppercase;background:#c00000;color:#fff;border:1px solid #a30000;white-space:nowrap;";
  const td = "padding:10px 12px;font-size:13px;border:1px solid #e5e7eb;vertical-align:middle;color:#111827;";
  const tdMono = `${td}font-family:Consolas,'Courier New',monospace;font-weight:600;`;

  const sorted = sortNoFlightsRowsByOperator(rows);

  const detailRows = sorted
    .map((r) => {
      const days =
        Number.isFinite(r?.togMinutes)
          ? formatTogDaysAmount(r.togMinutes)
          : formatTogDaysAmount(r?.tog);
      const loc = resolveNoFlightsFollowUpLocation(r);
      return `<tr>
        <td style="${tdMono}">${escapeHtml(cleanText(r.msn) || "—")}</td>
        <td style="${tdMono}">${escapeHtml(cleanText(r.reg) || "—")}</td>
        <td style="${td}white-space:nowrap;text-align:right;">${escapeHtml(days)}</td>
        <td style="${td}">${escapeHtml(loc)}</td>
      </tr>`;
    })
    .join("");

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f3f4f6;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f3f4f6;padding:24px 12px;">
    <tr><td align="center">
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:680px;background:#ffffff;border:1px solid #e5e7eb;border-radius:4px;overflow:hidden;">
        <tr><td style="background:#c00000;padding:14px 20px;">
          <div style="font-family:Calibri,Arial,sans-serif;font-size:12px;letter-spacing:0.12em;text-transform:uppercase;color:#ffffff;font-weight:700;">Dubai Aerospace Enterprise</div>
        </td></tr>
        <tr><td style="padding:20px 22px 8px;font-family:Calibri,Arial,sans-serif;">
          <h1 style="font-size:20px;margin:0 0 14px;color:#111827;font-weight:700;line-height:1.3;">${escapeHtml(subject)}</h1>
          <p style="font-size:14px;margin:0 0 12px;color:#111827;">Dear ${escapeHtml(cleanText(greetingName) || "colleague")},</p>
          <p style="font-size:14px;margin:0 0 12px;line-height:1.5;color:#374151;">${introHtml}</p>
          <p style="font-size:14px;margin:0 0 18px;line-height:1.5;color:#374151;">${askHtml}</p>
        </td></tr>
        <tr><td style="padding:0 22px 18px;font-family:Calibri,Arial,sans-serif;">
          <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;border:1px solid #e5e7eb;">
            <thead><tr>
              <th style="${th}">MSN</th>
              <th style="${th}">Registration</th>
              <th style="${th}">Days on ground</th>
              <th style="${th}">Location</th>
            </tr></thead>
            <tbody>${detailRows || `<tr><td colspan="4" style="${td}color:#6b7280;">No aircraft.</td></tr>`}</tbody>
          </table>
        </td></tr>
        <tr><td style="padding:4px 22px 20px;font-family:Calibri,Arial,sans-serif;">
          <p style="font-size:13px;color:#374151;margin:0;">Thank you,</p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body></html>`;
}

export function buildOperatorStatusCheckEmailHtml({
  contactName = "",
  row = null,
  generatedAt = new Date(),
} = {}) {
  const stamp = generatedAt instanceof Date ? generatedAt : new Date(generatedAt);
  const msn = cleanText(row?.msn) || "—";
  const reg = cleanText(row?.reg) || "—";
  const subject = `MSN ${msn} | ${reg} | STATUS CHECK`;
  const greetingName = cleanText(contactName).split(/\s+/)[0] || cleanText(contactName) || "Operator";
  return {
    subject,
    html: buildDaeInactivityFollowUpHtml({
      subject,
      greetingName,
      introHtml: `I hope you are doing well. This is to follow up on the status of <strong>MSN ${escapeHtml(msn)}</strong> (<strong>${escapeHtml(reg)}</strong>), which currently shows <strong>no recent flight activity</strong>.`,
      askHtml: "Could you please confirm the current status and any expected next flight date?",
      rows: row ? [row] : [],
      generatedAt: stamp,
    }),
  };
}

/** Outlook unsent HTML draft for a single-aircraft operator status check. */
export function buildOperatorStatusCheckEml({
  to = "",
  contactName = "",
  row = null,
  generatedAt = new Date(),
} = {}) {
  const stamp = generatedAt instanceof Date ? generatedAt : new Date(generatedAt);
  const { subject, html } = buildOperatorStatusCheckEmailHtml({ contactName, row, generatedAt: stamp });
  const msn = cleanText(row?.msn) || "aircraft";
  const lines = [
    "X-Unsent: 1",
    "From: ",
    `To: ${cleanText(to)}`,
    `Subject: ${subject}`,
    "MIME-Version: 1.0",
    'Content-Type: text/html; charset="utf-8"',
    "Content-Transfer-Encoding: 8bit",
    "",
    html,
    "",
  ];
  return {
    subject,
    to: cleanText(to),
    fileName: `ACTracker-status-check-MSN-${msn.replace(/[^\w.-]+/g, "_")}.eml`,
    blob: new Blob([lines.join("\n")], { type: "message/rfc822" }),
  };
}

export function buildNoFlightsFmFollowUpEml({
  fm = {},
  rows = [],
  generatedAt = new Date(),
} = {}) {
  const stamp = generatedAt instanceof Date ? generatedAt : new Date(generatedAt);
  const subject = noFlightsFmFollowUpSubject(stamp);
  const { week, year } = noFlightsReportWeekMeta(stamp);
  const to = cleanText(fm.email);
  const firstName = cleanText(fm.firstName) || cleanText(fm.displayName).split(/\s+/)[0] || "";
  const html = buildNoFlightsFmFollowUpEmailHtml({
    fmName: cleanText(fm.displayName) || cleanText(fm.initials),
    firstName,
    rows,
    generatedAt: stamp,
  });
  const initials = cleanText(fm.initials) || "FM";
  const lines = [
    "X-Unsent: 1",
    "From: ",
    `To: ${to}`,
    `Cc: ${NO_FLIGHTS_FM_FOLLOWUP_CC}`,
    `Subject: ${subject}`,
    "MIME-Version: 1.0",
    'Content-Type: text/html; charset="utf-8"',
    "Content-Transfer-Encoding: 8bit",
    "",
    html,
    "",
  ];
  return {
    subject,
    to,
    cc: NO_FLIGHTS_FM_FOLLOWUP_CC,
    fileName: `ACTracker-no-activity-5d-FM-${initials}-WK${week}-${year}.eml`,
    blob: new Blob([lines.join("\n")], { type: "message/rfc822" }),
  };
}
